import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, Store, NgxsSimpleChange } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { CategoryActions } from '../actions/category.actions';
import { Category } from '../models/category.model';
import { CategoryService } from 'src/app/services/category.service';
import { StorageService } from 'src/app/services/storage.service';
import { stateContentChanged, sameRows } from './state-content-changed'

export class CategoryStateModel {
    is_refreshing: boolean
    category: Category[]
    version?: string
}

@State<CategoryStateModel>({
    name: 'category',
    defaults: {
        is_refreshing: true,
        category: []
    }
})
@Injectable()
export class CategoryState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private categoryService: CategoryService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<CategoryStateModel>) {
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                        
                        await this.reloadStateFromStorage()                        
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {           
        if (this.storageService.is_initialized && stateContentChanged(change)) {   
            const state_model: CategoryStateModel = change.currentValue
            this.storageService.set('categories', JSON.stringify(state_model))
        }    
    }

    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('categories');
        if (json) {
            const state_model: CategoryStateModel = JSON.parse(json)
            if (state_model) {                
                this.store.dispatch(new CategoryActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new CategoryActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        // Send the version only with rows to go with it.
        const held = this.store.selectSnapshot(CategoryState.getState)
        const ret = await this.categoryService.getAll(held.category?.length ? held.version ?? '' : '')
        if (ret?.rows) {
          // The service has already run buildMany over the response, so the
          // rows arriving here are built models. Building them a second time
          // allocated a duplicate object for every row and, where buildOne
          // rebuilds children, re-walked those too.
          this.store.dispatch(new CategoryActions.Set(ret.rows, ret.version))
          return true
        }
        // Failed, or the list held is still current.
        return false
    }
  
    @Selector()
    static getState(state: CategoryStateModel) {
        return state
    }

    @Selector()
    static getIsRefreshing(state: CategoryStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getAll(state: CategoryStateModel) {
        return state.category
    }

    @Action(CategoryActions.Refresh)
    async refresh({patchState, getState}: StateContext<CategoryStateModel>, {  }:CategoryActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false, category: sameRows(getState().category)})
        }
    }
    
    @Action(CategoryActions.Init)
    init({setState, getState}: StateContext<CategoryStateModel>, { payload }:CategoryActions.Init) {
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new CategoryActions.Refresh())
    }

    @Action(CategoryActions.Set)
    set({setState}: StateContext<CategoryStateModel>, { payload, version }:CategoryActions.Set) {
        setState({    
            is_refreshing: false,        
            category: payload,
            version
        })
    }
}
